import React, { useState } from 'react';

import { FaPlus, FaTrash } from 'react-icons/fa';

// yyyy-MM-dd for native <input type="date">, keeping state as Date objects
// (same shape flowbite's Datepicker used) rather than reworking the rest
// of this form's date handling.
const toDateInputValue = (date: Date | null | undefined) => {
        if (!date) return '';
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
};

interface Option {
        value: string;
        label: string;
}

interface OperationReportFormData {
        patientOperationId: string | null;
        patientId: string;
        operationDate: Date;
        proceduralistId: string;
        assistantId: string;
        anaesthetistId: string;
        venueId: string;
        reportTitle: string;
        diagnosisIds: string[];
        itemIds: string[];
        procedureId: string;
        details: string;
}

interface Comment {
        date: Date;
        designationId: string;
        commentedById: string;
        comment: string;
}

type FormErrors = Partial<Record<keyof OperationReportFormData, string>>;

const CreateOperationReport = () => {
        const initialFormState: OperationReportFormData = {
                patientOperationId: null,
                patientId: '',
                operationDate: new Date(),
                proceduralistId: '',
                assistantId: '',
                anaesthetistId: '',
                venueId: '',
                reportTitle: '',
                diagnosisIds: [],
                itemIds: [],
                procedureId: '',
                details: '',
        };

        const [formData, setFormData] = useState<OperationReportFormData>(initialFormState);
        const [errors, setErrors] = useState<FormErrors>({});
        const [comments, setComments] = useState<Comment[]>([]);
        const [newComment, setNewComment] = useState<Comment>({
                date: new Date(),
                designationId: '',
                commentedById: '',
                comment: ''
        });

        // Sample data - replace with API calls
        const patients: Option[] = [
                { value: '1', label: 'John Smith' },
                { value: '2', label: 'Jane Doe' }
        ];

        const physicians: Option[] = [
                { value: '1', label: 'Dr. Smith' },
                { value: '2', label: 'Dr. Johnson' }
        ];

        const hospitals: Option[] = [
                { value: '1', label: 'General Hospital' },
                { value: '2', label: 'City Medical Center' }
        ];

        const diagnoses: Option[] = [
                { value: '1', label: 'Diagnosis 1' },
                { value: '2', label: 'Diagnosis 2' }
        ];

        const items: Option[] = [
                { value: '1', label: 'Item 1' },
                { value: '2', label: 'Item 2' }
        ];

        const procedures: Option[] = [
                { value: '1', label: 'Procedure 1' },
                { value: '2', label: 'Procedure 2' }
        ];

        const designations: Option[] = [
                { value: '1', label: 'Surgeon' },
                { value: '2', label: 'Nurse' }
        ];

        const validate = () => {
                const newErrors: FormErrors = {};
                if (!formData.patientId) newErrors.patientId = 'Patient is required';
                if (!formData.operationDate) newErrors.operationDate = 'Operation date is required';
                if (!formData.proceduralistId) newErrors.proceduralistId = 'Proceduralist is required';
                if (!formData.assistantId) newErrors.assistantId = 'Assistant is required';
                if (!formData.anaesthetistId) newErrors.anaesthetistId = 'Anaesthetist is required';
                if (!formData.venueId) newErrors.venueId = 'Hospital is required';
                if (!formData.reportTitle) newErrors.reportTitle = 'Report title is required';
                if (formData.diagnosisIds.length === 0) newErrors.diagnosisIds = 'At least one diagnosis is required';
                if (formData.itemIds.length === 0) newErrors.itemIds = 'At least one item code is required';
                if (!formData.procedureId) newErrors.procedureId = 'Procedure is required';
                if (!formData.details) newErrors.details = 'Details are required';

                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
        };

        const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                if (!validate()) return;

                try {
                        // TODO: Replace with actual API call
                        const response = await fetch('/api/operation-reports', {
                                method: 'POST',
                                headers: {
                                        'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                        ...formData,
                                        comments
                                }),
                        });

                        if (response.ok) {
                                // TODO: Handle success
                                console.log('Operation report created successfully');
                        } else {
                                // TODO: Handle error
                                console.error('Failed to create operation report');
                        }
                } catch (error) {
                        console.error('Error:', error);
                }
        };

        const handleCommentAdd = () => {
                if (!newComment.designationId || !newComment.commentedById || !newComment.comment) {
                        return;
                }

                // Check for duplicates
                const isDuplicate = comments.some(comment =>
                        comment.date === newComment.date &&
                        comment.designationId === newComment.designationId &&
                        comment.commentedById === newComment.commentedById &&
                        comment.comment === newComment.comment
                );

                if (isDuplicate) {
                        alert('Comment already exists.');
                        return;
                }

                setComments([...comments, { ...newComment }]);
                setNewComment({
                        date: new Date(),
                        designationId: '',
                        commentedById: '',
                        comment: ''
                });
        };

        const handleCommentRemove = (index: number) => {
                setComments(comments.filter((_, i) => i !== index));
        };

        return (
                <div className="p-6">
                        <div className="bg-base-100 rounded-lg shadow">
                                <div className="p-4 border-b">
                                        <h2 className="text-xl font-semibold">Create Operation Report</h2>
                                </div>

                                <form onSubmit={handleSubmit} className="p-4">
                                        {/* Patient Details Section */}
                                        <div className="mb-6">
                                                <h3 className="text-lg font-semibold border-b pb-2 mb-4">Patient Details</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Patient <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.patientId}
                                                                        onChange={e => setFormData({ ...formData, patientId: e.target.value })}
                                                                >
                                                                        <option value="">Select patient...</option>
                                                                        {patients.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.patientId && (
                                                                        <span className="text-error text-sm">{errors.patientId}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Operation Date <span className="text-error">*</span>
                                                                </label>
                                                                <input
                                                                        type="date"
                                                                        value={toDateInputValue(formData.operationDate)}
                                                                        onChange={e => setFormData({ ...formData, operationDate: e.target.value ? new Date(e.target.value) : formData.operationDate })}
                                                                        className="input input-bordered w-full"
                                                                />
                                                                {errors.operationDate && (
                                                                        <span className="text-error text-sm">{errors.operationDate}</span>
                                                                )}
                                                        </div>
                                                </div>
                                        </div>

                                        {/* Procedure Details Section */}
                                        <div className="mb-6">
                                                <h3 className="text-lg font-semibold border-b pb-2 mb-4">Procedure Details</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Proceduralist <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.proceduralistId}
                                                                        onChange={e => setFormData({ ...formData, proceduralistId: e.target.value })}
                                                                >
                                                                        <option value="">Select proceduralist...</option>
                                                                        {physicians.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.proceduralistId && (
                                                                        <span className="text-error text-sm">{errors.proceduralistId}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Assistant <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.assistantId}
                                                                        onChange={e => setFormData({ ...formData, assistantId: e.target.value })}
                                                                >
                                                                        <option value="">Select assistant...</option>
                                                                        {physicians.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.assistantId && (
                                                                        <span className="text-error text-sm">{errors.assistantId}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Anaesthetist <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.anaesthetistId}
                                                                        onChange={e => setFormData({ ...formData, anaesthetistId: e.target.value })}
                                                                >
                                                                        <option value="">Select anaesthetist...</option>
                                                                        {physicians.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.anaesthetistId && (
                                                                        <span className="text-error text-sm">{errors.anaesthetistId}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Hospital <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.venueId}
                                                                        onChange={e => setFormData({ ...formData, venueId: e.target.value })}
                                                                >
                                                                        <option value="">Select hospital...</option>
                                                                        {hospitals.map(h => (
                                                                                <option key={h.value} value={h.value}>{h.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.venueId && (
                                                                        <span className="text-error text-sm">{errors.venueId}</span>
                                                                )}
                                                        </div>
                                                </div>
                                        </div>

                                        {/* Report Details Section */}
                                        <div className="mb-6">
                                                <h3 className="text-lg font-semibold border-b pb-2 mb-4">Report Details</h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Report Title <span className="text-error">*</span>
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        className="input input-bordered w-full"
                                                                        value={formData.reportTitle}
                                                                        onChange={e => setFormData({ ...formData, reportTitle: e.target.value })}
                                                                />
                                                                {errors.reportTitle && (
                                                                        <span className="text-error text-sm">{errors.reportTitle}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Diagnosis <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        multiple
                                                                        className="select select-bordered w-full h-auto"
                                                                        value={formData.diagnosisIds}
                                                                        onChange={e => setFormData({
                                                                                ...formData,
                                                                                diagnosisIds: Array.from(e.target.selectedOptions, opt => opt.value)
                                                                        })}
                                                                >
                                                                        {diagnoses.map(d => (
                                                                                <option key={d.value} value={d.value}>{d.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.diagnosisIds && (
                                                                        <span className="text-error text-sm">{errors.diagnosisIds}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Item Code <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        multiple
                                                                        className="select select-bordered w-full h-auto"
                                                                        value={formData.itemIds}
                                                                        onChange={e => setFormData({
                                                                                ...formData,
                                                                                itemIds: Array.from(e.target.selectedOptions, opt => opt.value)
                                                                        })}
                                                                >
                                                                        {items.map(i => (
                                                                                <option key={i.value} value={i.value}>{i.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.itemIds && (
                                                                        <span className="text-error text-sm">{errors.itemIds}</span>
                                                                )}
                                                        </div>
                                                        <div>
                                                                <label className="block mb-2">
                                                                        Procedure <span className="text-error">*</span>
                                                                </label>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={formData.procedureId}
                                                                        onChange={e => setFormData({ ...formData, procedureId: e.target.value })}
                                                                >
                                                                        <option value="">Select procedure...</option>
                                                                        {procedures.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                                {errors.procedureId && (
                                                                        <span className="text-error text-sm">{errors.procedureId}</span>
                                                                )}
                                                        </div>
                                                </div>

                                                <div className="mt-4">
                                                        <label className="block mb-2">
                                                                Details <span className="text-error">*</span>
                                                        </label>
                                                        <textarea
                                                                className="textarea textarea-bordered w-full"
                                                                rows={4}
                                                                value={formData.details}
                                                                onChange={e => setFormData({ ...formData, details: e.target.value })}
                                                        />
                                                        {errors.details && (
                                                                <span className="text-error text-sm">{errors.details}</span>
                                                        )}
                                                </div>
                                        </div>

                                        {/* Comments Section */}
                                        <div className="mb-6">
                                                <h3 className="text-lg font-semibold border-b pb-2 mb-4">Comments</h3>

                                                {/* Add Comment Form */}
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                                        <div>
                                                                <input
                                                                        type="date"
                                                                        value={toDateInputValue(newComment.date)}
                                                                        onChange={e => setNewComment({ ...newComment, date: e.target.value ? new Date(e.target.value) : newComment.date })}
                                                                        className="input input-bordered w-full"
                                                                />
                                                        </div>
                                                        <div>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={newComment.designationId}
                                                                        onChange={e => setNewComment({ ...newComment, designationId: e.target.value })}
                                                                >
                                                                        <option value="">Select Designation</option>
                                                                        {designations.map(d => (
                                                                                <option key={d.value} value={d.value}>{d.label}</option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                        <div>
                                                                <select
                                                                        className="select select-bordered w-full"
                                                                        value={newComment.commentedById}
                                                                        onChange={e => setNewComment({ ...newComment, commentedById: e.target.value })}
                                                                >
                                                                        <option value="">Select Commented By</option>
                                                                        {physicians.map(p => (
                                                                                <option key={p.value} value={p.value}>{p.label}</option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                        <div className="flex-grow">
                                                                <input
                                                                        type="text"
                                                                        className="input input-bordered w-full"
                                                                        value={newComment.comment}
                                                                        onChange={e => setNewComment({ ...newComment, comment: e.target.value })}
                                                                        placeholder="Enter comment"
                                                                />
                                                        </div>
                                                        <div>
                                                                <button type="button" className="btn btn-warning btn-xs ml-2" onClick={handleCommentAdd}>
                                                                        <FaPlus />
                                                                </button>
                                                        </div>
                                                </div>

                                                {/* Comment List */}
                                                <table className="table table-compact table-zebra w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>Date</th>
                                                                        <th>Designation</th>
                                                                        <th>Commented By</th>
                                                                        <th>Comment</th>
                                                                        <th></th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {comments.map((comment, index) => (
                                                                        <tr key={index}>
                                                                                <td>{comment.date.toLocaleDateString()}</td>
                                                                                <td>{designations.find(d => d.value === comment.designationId)?.label}</td>
                                                                                <td>{physicians.find(p => p.value === comment.commentedById)?.label}</td>
                                                                                <td>{comment.comment}</td>
                                                                                <td>
                                                                                        <button type="button" className="btn btn-xs btn-error" onClick={() => handleCommentRemove(index)}>
                                                                                                <FaTrash />
                                                                                        </button>
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>

                                        <div className="flex justify-end space-x-4 mt-8">
                                                <button type="submit" className="btn btn-warning">
                                                        Submit
                                                </button>
                                                <button type="button" className="btn btn-secondary">
                                                        Cancel
                                                </button>
                                        </div>
                                </form>
                        </div>
                </div>
        );
};

export default CreateOperationReport;
